import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, Send, Users, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PayByCard } from "@/components/PayByCard";

export default function PayBalance() {
  const { bookingId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [loadState, setLoadState] = useState("loading");
  const [verifying, setVerifying] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/pay/${bookingId}`);
      setInfo(data);
      setLoadState("ready");
    } catch {
      setLoadState("error");
    }
  }, [bookingId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (params.get("cancelled")) toast.info("Payment cancelled — no charge was made.");
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    setVerifying(true);
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 8) { setVerifying(false); return; }
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setVerifying(false);
          toast.success("Payment received — thank you!");
          load();
          return;
        }
        setTimeout(poll, 2000);
      } catch { setVerifying(false); }
    };
    poll();
  }, [params, load]);

  if (loadState === "loading") {
    return <section className="max-w-lg mx-auto px-5 py-28 text-center"><Loader2 className="h-12 w-12 text-gold mx-auto animate-spin" /></section>;
  }
  if (loadState === "error" || !info) {
    return (
      <section className="max-w-lg mx-auto px-5 py-28 text-center" data-testid="pay-not-found">
        <h1 className="font-heading text-2xl font-bold text-white">Booking not found</h1>
        <p className="text-slate-400 mt-3">This link may be invalid or expired.</p>
        <button onClick={() => navigate("/")} className="mt-8 rounded-full bg-gold px-8 py-3.5 font-semibold text-navy hover:bg-gold-light transition-colors duration-200">Back to home</button>
      </section>
    );
  }

  const balance = info.balance_due;
  const settled = balance <= 0.01;

  return (
    <section className="max-w-xl mx-auto px-5 sm:px-8 py-20">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="rounded-sm bg-navy-light border border-gold/30 p-8 sm:p-10" data-testid="pay-balance-page">
        <span className="text-sm uppercase tracking-[0.2em] text-gold">Pay your booking</span>
        <h1 className="font-heading text-3xl font-bold text-white mt-1">{info.name}'s trip</h1>
        <p className="text-slate-400 mt-2 text-sm">{info.description}</p>

        <div className="mt-7 rounded-sm bg-navy border border-gold/20 p-6 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-slate-400">Date & time</span><span className="text-white">{info.date} at {info.time}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Total fare</span><span className="text-white">${info.amount.toFixed(2)} {info.currency}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Paid so far</span><span className="text-white">${info.amount_paid.toFixed(2)} {info.currency}</span></div>
          <div className="flex justify-between border-t border-gold/20 pt-2 mt-2"><span className="text-slate-300 font-medium">Balance remaining</span><span className="text-gold font-bold" data-testid="pay-balance-amount">${balance.toFixed(2)} {info.currency}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Reference</span><span className="text-white">{info.reference}</span></div>
        </div>

        {settled ? (
          <div className="mt-7 text-center rounded-sm bg-gold/10 border border-gold/40 p-8" data-testid="pay-settled">
            <CheckCircle2 className="h-14 w-14 text-gold mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold text-white">This booking is fully paid!</h2>
            <p className="text-slate-400 mt-2 text-sm">Thank you — nothing more to pay.</p>
          </div>
        ) : (
          <>
            {verifying && <div className="mt-5 flex items-center gap-2 text-sm text-gold" data-testid="pay-verifying"><Loader2 className="h-4 w-4 animate-spin" /> Confirming your last payment…</div>}
            <div className="mt-6"><PayByCard bookingId={bookingId} balance={balance} currency={info.currency} /></div>

            <div className="mt-6 rounded-sm bg-navy border border-gold/20 p-5" data-testid="pay-etransfer">
              <div className="flex items-center gap-2 mb-2"><Send className="h-4 w-4 text-gold" /><h3 className="font-heading text-sm font-bold text-white">Prefer Interac e-Transfer?</h3></div>
              <p className="text-xs text-slate-400 leading-relaxed">Send <span className="text-gold font-semibold">${balance.toFixed(2)} {info.currency}</span> to <span className="text-gold font-semibold break-all">{info.etransfer_email}</span> (auto-deposit). Add reference <span className="text-gold font-semibold">{info.reference}</span>.</p>
            </div>

            <button onClick={() => navigate(`/split/${bookingId}`)} data-testid="pay-goto-split"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full border border-gold/40 px-6 py-3 font-semibold text-gold transition-colors duration-200 hover:bg-gold/10">
              <Users className="h-4 w-4" /> Split it evenly with your group instead
            </button>
          </>
        )}

        <button onClick={() => navigate("/")} className="mt-7 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-gold transition-colors duration-200">
          Back to home <ArrowRight className="h-4 w-4" />
        </button>
      </motion.div>
    </section>
  );
}
